import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import Header from '../Header/header';
import Footer from '../Footer/footer';
import CompanyPurchaseCard from '../../components/PurchaseCompanyList/companyPurchaseCard';
import getPurchaseCompanies, { toCompanyFilterParams } from '../../services/purchase/getCompanies';
import cookie from '../../cookieHelper/cookie';
import { auth as AuthObject } from '../../services/login';
import './purchaseCompanyList.scss';

const PAGE_SIZE = 20;
const FILTER_DEBOUNCE_MS = 400;
const AUTH_ERROR_CODES = [ '10411', '10412', '10416', '10417' ];

export default function PurchaseCompanyList() {
    const [ searchParams, setSearchParams ] = useSearchParams();
    const requestedPage = Number(searchParams.get('page') || 1);
    const pageIndex = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage - 1 : 0;
    const filters = Object.fromEntries(['companyName', 'centralCompanyId', 'custId'].map(field => [field, searchParams.get(field) || '']));
    const setPage = nextPage => {
        const params = new URLSearchParams(searchParams);
        if (nextPage > 0) params.set('page', String(nextPage + 1));
        else params.delete('page');
        setSearchParams(params, { replace: true });
    };
    const [ attempt, setAttempt ] = useState(0);
    const [ data, setData ] = useState({ companies: [], total: 0 });
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState('');
    const [ sessionExpired, setSessionExpired ] = useState(false);
    const filterKey = JSON.stringify(toCompanyFilterParams(filters));
    const [ appliedFilterKey, setAppliedFilterKey ] = useState(filterKey);

    useEffect(() => {
        const timer = setTimeout(() => setAppliedFilterKey(filterKey), FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [ filterKey ]);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');
        getPurchaseCompanies(pageIndex, PAGE_SIZE, controller.signal, JSON.parse(appliedFilterKey))
            .then(result => {
                if (!controller.signal.aborted) setData(result);
            })
            .catch(err => {
                if (controller.signal.aborted) return;
                const code = String(err.response?.data?.status?.code);
                if (err.response?.status === 401 || AUTH_ERROR_CODES.includes(code)) {
                    cookie.delete_cookie(process.env.REACT_APP_COOKIE_NAME);
                    cookie.delete_cookie('loginProfileInfo');
                    AuthObject.isAuthenticated = false;
                    setSessionExpired(true);
                    return;
                }
                setError(err.response?.status === 422
                    ? 'Invalid filter value. Please check the filters and try again.'
                    : 'Unable to load companies. Please try again.');
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [ attempt, pageIndex, appliedFilterKey ]);

    const total = data.total;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const page = pageIndex;
    const visibleCompanies = data.companies;

    // A page beyond the last one (stale URL or shrunken result set) jumps back to the last page.
    useEffect(() => {
        if (!loading && !error && total > 0 && pageIndex >= totalPages) setPage(totalPages - 1);
    }, [ loading, error, total, pageIndex, totalPages ]);

    if (sessionExpired) return <Navigate to="/" replace />;

    const updateFilter = (field, rawValue) => {
        const value = field === 'centralCompanyId' ? rawValue.replace(/\D/g, '') : rawValue;
        const params = new URLSearchParams(searchParams);
        if (value) params.set(field, value);
        else params.delete(field);
        params.delete('page');
        setSearchParams(params, { replace: true });
    };
    return (
      <div className="bodyWrapper purchase-company-shell">
        <Header />
        <main className="purchase-company-page purchase-company-directory">
          <div className="purchase-company-heading">
            <span className="purchase-company-eyebrow">STORY MANAGEMENT</span>
            <h1>Company Purchases</h1>
          </div>
          <div className="purchase-company-filters" role="search" aria-label="Filter companies">
            {[
                [ 'companyName', 'Company name', 'Search company name', 'building-o', 256 ],
                [ 'centralCompanyId', 'Central company ID', 'Search central company ID', 'hashtag', 18 ],
                [ 'custId', 'Customer ID', 'Search customer ID', 'user-o', 128 ],
            ].map(([ field, label, placeholder, icon, maxLength ]) => (
              <label key={field} htmlFor={`filter-${field}`}>
                {label}
                <span className="purchase-filter-input">
                  <i className={`fa fa-${icon}`} aria-hidden="true" />
                  <input id={`filter-${field}`} type="search" value={filters[ field ]} maxLength={maxLength}
                    inputMode={field === 'centralCompanyId' ? 'numeric' : undefined}
                    placeholder={placeholder} onChange={event => updateFilter(field, event.target.value)} />
                </span>
              </label>
            ))}
            <button type="button" disabled={!Object.values(filters).some(Boolean)} onClick={() => {
                const params = new URLSearchParams(searchParams);
                ['companyName', 'centralCompanyId', 'custId', 'page'].forEach(field => params.delete(field));
                setSearchParams(params, { replace: true });
            }}><i className="fa fa-repeat" aria-hidden="true" />Clear filters</button>
          </div>
          {loading && <p role="status">Loading companies…</p>}
          {!loading && error && <div role="alert" className="purchase-company-error">
            <p>{error}</p>
            <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>
            </div>}
          {!loading && !error && <>
            {!total && <p>No companies found.</p>}
            {visibleCompanies.map(company => (
              <CompanyPurchaseCard key={company.centralCompanyId} company={company} />
                    ))}
            <p className="purchase-company-total" role="status">{total} {total === 1 ? 'company' : 'companies'}</p>
            </>}
          {!error && (total > PAGE_SIZE || page > 0) && <nav className="purchase-company-pagination" aria-label="Company pagination">
            <button type="button" disabled={loading || page === 0} onClick={() => setPage(page - 1)}>Previous</button>
            <span>Page {page + 1} of {totalPages}</span>
            <button type="button" disabled={loading || page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
            </nav>}
        </main>
        <Footer />
      </div>
    );
}
